import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Col, Row, Spin } from 'antd';
import { asyncConnect, withAsyncHandlers } from 'react-async-client';
import Translate, { translate } from 'react-translate-component';
import { head, any, prop, propEq, pathOr, isEmpty, path } from 'ramda';
import * as yup from 'yup';

import { getUser, createEvent, getAvailableEventTypes } from 'actions/asyncActions';
import ErrorHTML from 'components/common/form/ErrorHTML';
import EventFields from './card/EventFields';
import EventResultFields from './card/EventResultFields';
import EventDocumentPickField from './card/EventDocumentPickField';
import withFormWrapper from 'components/hocs/withFormWrapper';
import { getEventConfig } from 'helpers/eventHelper';
import ListenerField from 'components/common/form/ListenerField';

const validationSchema = () => {
    return yup.object().shape({
        contact: yup.object().shape({
            type: yup.number().required(),
            employee: yup.number().required(),
        }),
        details: yup.object().shape({
            dateTo: yup.string().required('enterDate')
        })
    });
};

const getResponsible = (user, participators) => (
    participators && any(propEq('id', user.id), participators) ? user.id : head(participators).id
);

const stateToProps = (state, { params }) => ({
    initialValues: {
        contact: {
            type: params.type,
            person: params.person.id,
            project: params.project.id,
            employee: getResponsible(getUser.selectData(state), params.participators)
        },
        details: {
            dateTo: new Date().toLocaleDateString('ru')
        }
    }
});

@asyncConnect({
    formAction: createEvent
        .withSuccessHandler(props => props.close())
        .withOptions({ resetOnUnmount: true }),
    getAvailableEventTypes: getAvailableEventTypes
        .withPayload(({ params: { person, project } }) => ({
            project: project.id,
            person: person.id,
        }))
        .withOptions({ dispatchOnMount: true }),
}, stateToProps)
@withFormWrapper({
    mapPropsToValues: props => props.initialValues,
    validationSchema
})
@withAsyncHandlers({
    getAvailableEventTypes: getAvailableEventTypes
        .withSuccessHandler(({ form, params }, { payload }) => {
            const eventType = head(payload);

            if (eventType && !params.type) {
                form.change('contact.type', eventType.workCode);
            }
        }),
})
export default class CreateEventModal extends Component {
    static propTypes = {
        modal: PropTypes.object.isRequired,
        params: PropTypes.shape({
            person: PropTypes.object.isRequired,
            project: PropTypes.object.isRequired,
            participators: PropTypes.array.isRequired,
            documentsList: PropTypes.array,
            withDocument: PropTypes.bool,
            type: PropTypes.string,
            typeName: PropTypes.string,
            item: PropTypes.object,
        }),
        formAction: PropTypes.object,
        getAvailableEventTypes: PropTypes.object,
        form: PropTypes.object,
        handleSubmit: PropTypes.func,
    };

    renderTitle() {
        const { person, withDocument, documentsList } = this.props.params;

        return (
            <Translate
                content={`events.create.title${withDocument ? 'Enter' : documentsList ? 'Pick' : ''}`}
                with={{ person: prop('text', person) }}
            />
        );
    }

    renderFooter() {
        const { formAction: { meta }, handleSubmit, params } = this.props;
        const showSaveButton = !params.withDocument;

        return (showSaveButton || meta.error) ? (
            <Row>
                <Col xs={{ span: 12 }} className="alert-inline">
                    <ErrorHTML meta={meta} />
                </Col>
                <Col xs={{ span: 12 }}>
                    { showSaveButton && (
                        <Button type='primary' onClick={handleSubmit} disabled={meta.pending}>
                            <Translate content='modal_settings.save' />
                        </Button>
                    )}
                </Col>
            </Row>
        ) : null;
    }

    showEmptyEventsMessage = () => {
        const { getAvailableEventTypes } = this.props;
        const success = path(['getAvailableEventTypes', 'meta', 'success'], this.props);
        const emptyEvents = isEmpty(getAvailableEventTypes.data);

        return success && emptyEvents;
    }

    renderEventForm = () => {
        const { params: { person, project, participators, type, typeName, withDocument, item, documentsList } } = this.props;
        const { formAction, form, getAvailableEventTypes, handleSubmit } = this.props;

        return (
            this.showEmptyEventsMessage() ? (
                <div className='empty-event-message'>
                    { translate('worksheetMass.messages.emptyEvents') }
                </div>
            ) : (

                <ListenerField
                    listenFieldName={['contact.type', 'contact.organization']}
                >
                    { (values) => {
                        const event = getEventConfig(values.contact.type, getAvailableEventTypes.data);

                        return (
                            <Fragment>
                                <EventFields
                                    person={person}
                                    project={project}
                                    participators={participators}
                                    type={type}
                                    typeName={typeName}
                                    eventConfig={pathOr({}, ['view'], event)}
                                    change={form.change}
                                    eventTypes={getAvailableEventTypes.data}
                                    getAvailableEventTypes={getAvailableEventTypes}
                                    organization={values.contact.organization}
                                    xmlType={event.xmlType}
                                />
                                { withDocument && (
                                    <ListenerField
                                        listenFieldName={['shortResultBlock']}
                                    >
                                        { (values) => (
                                            <EventResultFields
                                                formAction={formAction}
                                                event={{
                                                    ...event,
                                                    ...item,
                                                }}
                                                values={values}
                                                form={form}
                                                type={event.xmlType}
                                                handleSubmit={handleSubmit}
                                            />
                                        )}
                                    </ListenerField>
                                )}
                                { documentsList && (
                                    <ListenerField
                                        listenFieldName={['result.dockey']}
                                    >
                                        {({ result: { dockey } }) => (
                                            <EventDocumentPickField
                                                type={event.xmlType}
                                                documentsList={documentsList}
                                                dockey={dockey}
                                                form={form}
                                            />
                                        )}
                                    </ListenerField>
                                )}
                            </Fragment>
                        );
                    }}

                </ListenerField>
            )
        );
    }

    render() {
        const { modal, getAvailableEventTypes } = this.props;
        const eventTypesSuccess = path(['meta', 'success'], getAvailableEventTypes);

        return (
            <Modal
                {...modal}
                title={this.renderTitle()}
                footer={!this.showEmptyEventsMessage() && this.renderFooter()}
            >
                { eventTypesSuccess ? (
                    this.renderEventForm()
                ) : (
                    <Spin
                        spinning={true}
                        className='event-form-spiner'
                    />
                )}
            </Modal>
        );
    }
}
